import React, { useMemo, useState } from 'react';
import SectionCard from '../controls/SectionCard';
import Row from '../controls/Row';
import Toggle from '../controls/Toggle';
import Slider from '../controls/Slider';
import NumberInput from '../controls/NumberInput';
import Button from '../controls/Button';
import ColorPanel from '../../ColorPanel/ColorPanel';
import { normalizePaint } from '../../../utils/paintUtils';
import { computeCoverScale } from '../../../utils/frameUtils';

const FrameStyleSection = ({
  model,
  palette,
  brandColors,
  onUsePaint,
  onRequestEyedropper,
  disabled,
  frameCropState,
  onStartFrameCrop,
  onCommitFrameCrop,
  onCancelFrameCrop,
  onResetFrameCrop,
  onFrameContentScaleChange,
  debugMode,
  onDebugFillFrame,
  assets,
  assetsLoading,
  onSelectFrameAsset,
}) => {
  const frames = model.selectedElements || [];
  const singleFrame = frames.length === 1 ? frames[0] : null;
  const hasContent = Boolean(singleFrame?.content?.type === 'image');
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const isCropping = Boolean(
    frameCropState?.active && singleFrame && frameCropState.frameId === singleFrame.id,
  );

  const backgroundPaint = model.mixed('backgroundPaint');
  const fillEnabled = model.mixed('fillEnabled');
  const borderEnabled = model.mixed('frame.border.enabled');
  const borderColor = model.mixed('frame.border.color');
  const borderWidth = model.mixed('frame.border.width');
  const shadowEnabled = model.mixed('frame.shadow.enabled');
  const shadowX = model.mixed('frame.shadow.x');
  const shadowY = model.mixed('frame.shadow.y');
  const shadowBlur = model.mixed('frame.shadow.blur');
  const shadowOpacity = model.mixed('frame.shadow.opacity');
  const cornerRadius = model.mixed('frame.cornerRadius');

  const handleToggle = (path, mixedValue) => {
    const nextValue = mixedValue.kind === 'mixed' ? true : !mixedValue.value;
    model.applyToSelection((el) => {
      if (path === 'border') {
        return {
          frame: {
            border: {
              ...(el.frame?.border || {}),
              enabled: nextValue,
            },
          },
        };
      }
      if (path === 'shadow') {
        return {
          frame: {
            shadow: {
              ...(el.frame?.shadow || {}),
              enabled: nextValue,
            },
          },
        };
      }
      return null;
    }, 'immediate', `frame-${path}-toggle`);
  };

  const applyBackgroundPaint = (paint) => {
    const normalized = normalizePaint(paint, '#F6F2E8');
    model.applyToSelection({ backgroundPaint: normalized }, 'debounced', 'frame-background');
  };

  const handleFillToggle = () => {
    const nextValue = fillEnabled.kind === 'mixed' ? true : !fillEnabled.value;
    model.applyToSelection({ fillEnabled: nextValue }, 'immediate', 'frame-fill-toggle');
  };

  const applyBorderPaint = (paint) => {
    const normalized = normalizePaint(paint, '#FFFFFF');
    const color =
      normalized.type === 'solid'
        ? normalized.color
        : normalized.stops?.[0]?.color || '#FFFFFF';
    model.applyToSelection((el) => ({
      frame: {
        border: {
          ...(el.frame?.border || {}),
          enabled: true,
          color,
        },
      },
    }), 'debounced', 'frame-border-color');
  };

  const handleBorderWidthChange = (value, commitMode = 'commitOnRelease') => {
    const safeValue = Math.max(0, Math.min(200, value));
    model.applyToSelection((el) => ({
      frame: {
        border: {
          ...(el.frame?.border || {}),
          enabled: safeValue > 0 ? true : el.frame?.border?.enabled,
          width: safeValue,
        },
      },
    }), commitMode, 'frame-border-width');
  };

  const handleShadowBlurChange = (value) => {
    model.applyToSelection((el) => ({
      frame: {
        shadow: {
          ...(el.frame?.shadow || {}),
          enabled: true,
          blur: Math.max(0, Math.min(200, value)),
        },
      },
    }), 'commitOnRelease', 'frame-shadow-blur');
  };

  const handleShadowOffsetChange = (axis, value) => {
    const safeValue = Math.max(-400, Math.min(400, value));
    model.applyToSelection((el) => ({
      frame: {
        shadow: {
          ...(el.frame?.shadow || {}),
          enabled: true,
          [axis]: safeValue,
        },
      },
    }), 'commitOnRelease', `frame-shadow-${axis}`);
  };

  const handleShadowOpacityChange = (value) => {
    const opacity = Math.max(0, Math.min(1, value / 100));
    model.applyToSelection((el) => ({
      frame: {
        shadow: {
          ...(el.frame?.shadow || {}),
          enabled: true,
          opacity,
        },
      },
    }), 'commitOnRelease', 'frame-shadow-opacity');
  };

  const zoomInfo = useMemo(() => {
    const content =
      isCropping && frameCropState?.draftContent ? frameCropState.draftContent : singleFrame?.content;
    if (!singleFrame || !content) return null;
    const naturalW = content.naturalW || singleFrame.w;
    const naturalH = content.naturalH || singleFrame.h;
    const coverScale = computeCoverScale(singleFrame.w, singleFrame.h, naturalW, naturalH);
    const currentScale = content.scale || coverScale;
    return {
      coverScale,
      currentScale,
      maxScale: coverScale * 5,
    };
  }, [frameCropState?.draftContent, isCropping, singleFrame]);

  const showCornerRadius = singleFrame?.frame?.shapeType === 'rect';
  const maxCornerRadius = singleFrame
    ? Math.max(0, Math.min(singleFrame.w, singleFrame.h) / 2)
    : 0;

  const imageAssets = useMemo(
    () => (assets || []).filter((asset) => asset.type !== 'pdf'),
    [assets],
  );

  return (
    <SectionCard title="Frame">
      <Row label="Fill">
        <Toggle
          checked={Boolean(fillEnabled.value)}
          indeterminate={fillEnabled.kind === 'mixed'}
          onChange={handleFillToggle}
          label="Fill"
          disabled={disabled}
        />
        <ColorPanel
          label="Fill"
          value={backgroundPaint.value}
          mixed={backgroundPaint.kind === 'mixed'}
          allowGradient
          documentColors={palette?.documentColors}
          recentColors={palette?.recentColors}
          recentGradients={palette?.recentGradients}
          brandColors={brandColors}
          onChange={applyBackgroundPaint}
          onCommit={applyBackgroundPaint}
          onUsePaint={onUsePaint}
          onRequestEyedropper={
            onRequestEyedropper
              ? () => onRequestEyedropper((paint) => applyBackgroundPaint(paint))
              : undefined
          }
          disabled={disabled || !fillEnabled.value}
        />
      </Row>

      <Row label="Border">
        <Toggle
          checked={Boolean(borderEnabled.value)}
          indeterminate={borderEnabled.kind === 'mixed'}
          onChange={() => handleToggle('border', borderEnabled)}
          label="Border"
          disabled={disabled}
        />
        {Boolean(borderEnabled.value) && (
          <ColorPanel
            label="Border"
            value={{
              type: 'solid',
              color: borderColor.value || '#FFFFFF',
              alpha: 100,
            }}
            mixed={borderColor.kind === 'mixed'}
            allowGradient
            documentColors={palette?.documentColors}
            recentColors={palette?.recentColors}
            recentGradients={palette?.recentGradients}
            brandColors={brandColors}
            onChange={applyBorderPaint}
            onCommit={applyBorderPaint}
            onUsePaint={onUsePaint}
            onRequestEyedropper={
              onRequestEyedropper
                ? () => onRequestEyedropper((paint) => applyBorderPaint(paint))
                : undefined
            }
            disabled={disabled}
          />
        )}
      </Row>

      <Row label="Border width">
        <NumberInput
          value={borderWidth.value ?? 0}
          mixed={borderWidth.kind === 'mixed'}
          min={0}
          max={200}
          onCommit={(value) => handleBorderWidthChange(value, 'debounced')}
          disabled={disabled}
        />
      </Row>

      {showCornerRadius && (
        <Row label="Corner radius">
          <Slider
            min={0}
            max={Math.max(0, Math.round(maxCornerRadius))}
            step={1}
            value={cornerRadius.kind === 'mixed' ? 0 : cornerRadius.value || 0}
            mixed={cornerRadius.kind === 'mixed'}
            onChange={(value) => {
              const safe = Math.max(0, Math.min(maxCornerRadius, value));
              model.applyToSelection((el) => ({
                frame: {
                  ...(el.frame || {}),
                  cornerRadius: safe,
                },
              }), 'commitOnRelease', 'frame-corner-radius');
            }}
            onCommit={() => model.commitPending('frame-corner-radius')}
            disabled={disabled}
          />
        </Row>
      )}

      <Row label="Shadow">
        <Toggle
          checked={Boolean(shadowEnabled.value)}
          indeterminate={shadowEnabled.kind === 'mixed'}
          onChange={() => handleToggle('shadow', shadowEnabled)}
          label="Shadow"
          disabled={disabled}
        />
      </Row>

      {Boolean(shadowEnabled.value) && (
        <>
          <Row label="Shadow X">
            <Slider
              min={-100}
              max={100}
              step={1}
              value={shadowX.kind === 'mixed' ? 0 : shadowX.value || 0}
              mixed={shadowX.kind === 'mixed'}
              onChange={(value) => handleShadowOffsetChange('x', value)}
              onCommit={() => model.commitPending('frame-shadow-x')}
              disabled={disabled}
            />
          </Row>
          <Row label="Shadow Y">
            <Slider
              min={-100}
              max={100}
              step={1}
              value={shadowY.kind === 'mixed' ? 0 : shadowY.value || 0}
              mixed={shadowY.kind === 'mixed'}
              onChange={(value) => handleShadowOffsetChange('y', value)}
              onCommit={() => model.commitPending('frame-shadow-y')}
              disabled={disabled}
            />
          </Row>
          <Row label="Shadow blur">
            <Slider
              min={0}
              max={200}
              step={1}
              value={shadowBlur.kind === 'mixed' ? 0 : shadowBlur.value || 0}
              mixed={shadowBlur.kind === 'mixed'}
              onChange={handleShadowBlurChange}
              onCommit={() => model.commitPending('frame-shadow-blur')}
              disabled={disabled}
            />
          </Row>
          <Row label="Shadow opacity">
            <Slider
              min={0}
              max={100}
              step={1}
              value={shadowOpacity.kind === 'mixed' ? 0 : Math.round((shadowOpacity.value || 0) * 100)}
              mixed={shadowOpacity.kind === 'mixed'}
              onChange={handleShadowOpacityChange}
              onCommit={() => model.commitPending('frame-shadow-opacity')}
              disabled={disabled}
            />
          </Row>
        </>
      )}

      {singleFrame && (
        <Row label="Image">
          <div className="studio-properties-inline-actions">
            <Button
              variant="default"
              onClick={() => setAssetPickerOpen((prev) => !prev)}
              disabled={disabled}
            >
              {hasContent ? 'Replace image' : 'Add image'}
            </Button>
            {!isCropping ? (
              <Button
                variant="default"
                onClick={() => onStartFrameCrop?.(singleFrame.id)}
                disabled={disabled || !hasContent}
              >
                Reposition
              </Button>
            ) : (
              <>
                <Button variant="primary" onClick={onCommitFrameCrop} disabled={disabled}>
                  Done
                </Button>
                <Button variant="ghost" onClick={onCancelFrameCrop} disabled={disabled}>
                  Cancel
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={onResetFrameCrop} disabled={disabled || !hasContent}>
              Reset
            </Button>
            {debugMode && (
              <Button
                variant="ghost"
                onClick={() => onDebugFillFrame?.(singleFrame.id)}
                disabled={disabled}
              >
                Debug: Fill
              </Button>
            )}
          </div>
        </Row>
      )}

      {singleFrame && assetPickerOpen && (
        <div className="studio-frame-asset-picker">
          <div className="studio-frame-asset-header">
            <span>Choose an image</span>
            <button
              type="button"
              className="studio-frame-asset-close"
              onClick={() => setAssetPickerOpen(false)}
            >
              ×
            </button>
          </div>
          {assetsLoading ? (
            <div className="studio-frame-asset-empty">Loading uploads...</div>
          ) : imageAssets.length === 0 ? (
            <div className="studio-frame-asset-empty">No uploads yet.</div>
          ) : (
            <div className="studio-frame-asset-grid">
              {imageAssets.slice(0, 12).map((asset) => (
                <button
                  type="button"
                  key={asset.id}
                  className="studio-frame-asset-tile"
                  onClick={() => {
                    onSelectFrameAsset?.(singleFrame.id, asset);
                    setAssetPickerOpen(false);
                  }}
                >
                  <img
                    src={asset.thumbUrl || asset.downloadUrl}
                    alt={asset.originalName || 'Upload'}
                    className="studio-frame-asset-thumb"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {singleFrame && hasContent && zoomInfo && (
        <Row label="Zoom">
          <Slider
            min={zoomInfo.coverScale}
            max={zoomInfo.maxScale}
            step={0.01}
            value={zoomInfo.currentScale}
            onChange={(value) => onFrameContentScaleChange?.(value)}
            disabled={disabled}
          />
        </Row>
      )}

      {singleFrame && hasContent && (
        <div className="studio-frame-help">
          Use Reposition, then drag inside the frame to move the image. Scroll to zoom. (Alt + drag works too.)
        </div>
      )}
    </SectionCard>
  );
};

export default FrameStyleSection;
